import axiosRetry from 'axios-retry';
import { computeBaseUrls, createHttpClient } from './create-http-client';
import type { ScribeHttpClientDeps, ScribeHttpClients } from './types';

export function createScribeHttpClients(deps: ScribeHttpClientDeps): ScribeHttpClients {
  const urls = computeBaseUrls(deps);

  return {
    scribeHttp: createHttpClient(deps, {
      target: 'v1',
      auth: { type: 'session', allowExplicitToken: true },
      workspace: 'always',
      tempUser: true,
      errorTier: 'tier1',
      // retries: 0 is LOAD-BEARING — do not increase. The Tier-1 response
      // handler stamps __authRetryAttempted on the config before calling retry().
      retry: 0,
    }),
    identityBrokerHttp: createHttpClient(deps, {
      target: 'identityBroker',
      auth: {
        type: 'session',
        emptyBehavior: 'omit',
        clientIdentityCondition: 'when-authenticated',
      },
      errorTier: 'tier1',
    }),
    identityBrokerPublicHttp: createHttpClient(deps, {
      target: 'identityBroker',
      auth: 'none',
    }),
    scribeHttpV2: createHttpClient(deps, {
      target: 'v2',
      auth: { type: 'session', emptyBehavior: 'omit' },
      // Preserve explicit caller-provided workspace header over the atom getter
      workspace: 'preserveCaller',
      tempUser: true,
      errorTier: 'tier1',
      // retries: 0 is LOAD-BEARING — do not increase.
      retry: 0,
    }),
    scribeHttpV2_1: createHttpClient(deps, {
      target: 'v2_1',
      auth: 'session',
      workspace: 'always',
      errorTier: 'tier1',
    }),
    scribeHttpV2_2: createHttpClient(deps, {
      target: 'v2_2',
      auth: 'session',
      workspace: 'always',
      errorTier: 'tier1',
    }),
    openApiHttp: createHttpClient(deps, {
      target: 'openApi',
      auth: 'widget',
      errorTier: 'tier2',
      retry: {
        retries: 3,
        // Exclude configs carrying __authRetryAttempted from replaying
        retryCondition: (error) =>
          !error.config?.__authRetryAttempted &&
          (axiosRetry.isNetworkOrIdempotentRequestError(error) ||
            error.response?.status === 429),
      },
    }),
    linkShareHttp: createHttpClient(deps, {
      target: 'linkShare',
      auth: 'none',
      retry: {
        retries: 3,
        // Exclude configs carrying __authRetryAttempted from replaying
        retryCondition: (error) =>
          !error.config?.__authRetryAttempted &&
          (error.response?.status === 429 ||
            (error.config?.method?.toUpperCase() === 'GET' &&
              axiosRetry.isNetworkOrIdempotentRequestError(error))),
      },
    }),
    ...urls,
  };
}
